import { useEffect, useState } from "react";
import { api, formatApiError, money } from "../lib/api";
import { PageHeader, Card, StatCard, Modal, Field, inputClass, EmptyState, PrimaryButton } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Coins, Plus, Trash, CalendarBlank, ChartBar } from "@phosphor-icons/react";

const CATEGORIES = ["Renta", "Servicios", "Nómina", "Limpieza", "Publicidad", "Seguridad", "General"];

export default function FixedExpenses() {
  const { user } = useAuth();
  const isManager = true;
  const canDelete = ["admin", "gerente_general"].includes(user?.role) || !!user?.can_delete;
  const [data, setData] = useState({ items: [], monthly_total: 0, annual_total: 0 });
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ concept: "", category: "General", amount_monthly: "", notes: "" });

  const load = () => api.get("/fixed-expenses").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/fixed-expenses", { ...form, amount_monthly: parseFloat(form.amount_monthly || 0) });
      toast.success("Gasto fijo agregado"); setOpen(false);
      setForm({ concept: "", category: "General", amount_monthly: "", notes: "" }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id) => { if (!window.confirm("¿Eliminar gasto?")) return; await api.delete(`/fixed-expenses/${id}`); load(); };

  return (
    <div>
      <PageHeader overline="Gastos recurrentes de obra" title="Gastos fijos"
        action={isManager && <PrimaryButton onClick={() => setOpen(true)} data-testid="add-expense-btn"><Plus size={18} weight="bold" /> Nuevo gasto</PrimaryButton>} />

      <div className="grid grid-cols-2 gap-4 mb-6">
        <StatCard label="Total mensual" value={money(data.monthly_total)} icon={CalendarBlank} tone="orange" />
        <StatCard label="Total anual" value={money(data.annual_total)} icon={ChartBar} />
      </div>

      {data.items.length === 0 ? (
        <EmptyState icon={Coins} title="Sin gastos fijos" subtitle="Registra rentas (sanitarios, casetas), servicios (agua, vigilancia), nómina, limpieza, publicidad, etc. para ver el total mensual y anual."
          action={isManager && <PrimaryButton onClick={() => setOpen(true)}><Plus size={18} weight="bold" /> Nuevo gasto</PrimaryButton>} />
      ) : (
        <Card className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                {["Concepto", "Categoría", "Mensual", "Anual", "Notas", ""].map((h) => <th key={h} className="px-4 py-3 overline">{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.map((i) => (
                <tr key={i.id} data-testid={`expense-row-${i.id}`} className="border-b border-border last:border-0 hover:bg-muted/50 transition-colors duration-150">
                  <td className="px-4 py-3 font-semibold">{i.concept}</td>
                  <td className="px-4 py-3"><span className="inline-flex px-2 py-0.5 rounded-sm text-xs font-medium bg-slate-100 text-slate-700">{i.category}</span></td>
                  <td className="px-4 py-3 font-medium">{money(i.amount_monthly)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{money((i.amount_monthly || 0) * 12)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{i.notes || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {canDelete && <button onClick={() => remove(i.id)} className="p-1.5 text-muted-foreground hover:text-danger"><Trash size={18} /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-heading font-bold bg-muted/40">
                <td className="px-4 py-3" colSpan={2}>Total</td>
                <td className="px-4 py-3 text-primary">{money(data.monthly_total)}</td>
                <td className="px-4 py-3">{money(data.annual_total)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </Card>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo gasto fijo">
        <form onSubmit={save} className="space-y-4">
          <Field label="Concepto *"><input data-testid="expense-concept" required className={inputClass} placeholder="Ej. Renta de sanitarios" value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Categoría">
              <select data-testid="expense-category" className={inputClass} value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Monto mensual (MXN) *"><input data-testid="expense-amount" type="number" step="0.01" required className={inputClass} value={form.amount_monthly} onChange={(e) => setForm({ ...form, amount_monthly: e.target.value })} /></Field>
          </div>
          <Field label="Notas"><input className={inputClass} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          <PrimaryButton type="submit" disabled={busy} data-testid="save-expense-btn">{busy ? "Guardando…" : "Guardar"}</PrimaryButton>
        </form>
      </Modal>
    </div>
  );
}
